import React, { useState, useEffect } from 'react';
import { ArrowDown, Zap, ArrowRight } from 'lucide-react';

interface HeroSectionProps {
  onOpenConsultation?: () => void;
  onNavigateCalculator?: () => void;
}

export const HeroSection: React.FC<HeroSectionProps> = ({ onNavigateCalculator }) => {
  const baseSlotItems = [
    { label: '지붕', color: 'text-sky-400' },
    { label: '토지', color: 'text-emerald-400' },
    { label: '주차장', color: 'text-sky-500' },
    { label: '옥상', color: 'text-purple-400' },
    { label: '축사', color: 'text-amber-400' },
    { label: '공장', color: 'text-teal-400' },
  ];

  const [index, setIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % baseSlotItems.length);
    }, 2800);
    return () => clearInterval(timer);
  }, [baseSlotItems.length]);

  const scrollToNextSection = () => {
    const elem = document.getElementById('growth-section');
    if (elem) {
      elem.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen w-full flex flex-col items-center justify-between pt-28 pb-12 overflow-hidden bg-slate-950 text-white">
      
      {/* 1. Clear Full-Screen Video Background */}
      <video
        autoPlay
        loop
        muted
        playsInline
        poster="/solar_roof_hero.png"
        className="absolute inset-0 w-full h-full object-cover object-center z-0 opacity-75 scale-100"
      >
        <source src="/hero_video.mp4" type="video/mp4" />
      </video>

      {/* Subtle Gradient Fade for Header and Bottom contrast only */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950/70 via-transparent to-slate-950/90 z-0 pointer-events-none" />

      <div className="w-full max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 my-auto text-center flex flex-col items-center justify-center">
        
        {/* 2. Slot Machine Headline (빈 [회전 룰렛] 빌려주고,) */}
        <div className="space-y-4 my-2 max-w-5xl mx-auto">
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black text-white tracking-tight leading-[1.2] keep-all drop-shadow-2xl flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
            <span>빈</span>
            
            {/* 1.5X (3/2 ratio) Borderless Rolling Slot Reel Container */}
            <div className="relative inline-flex items-center justify-center min-w-[130px] sm:min-w-[200px] lg:min-w-[270px] h-[1.25em] overflow-hidden align-middle">
              {baseSlotItems.map((item, idx) => {
                const isCurrent = idx === index;
                const isPrev = idx === (index - 1 + baseSlotItems.length) % baseSlotItems.length;

                let animationClass = 'translate-y-full opacity-0 pointer-events-none';
                if (isCurrent) {
                  animationClass = 'translate-y-0 opacity-100 transition-all duration-500 ease-out';
                } else if (isPrev) {
                  animationClass = '-translate-y-full opacity-0 transition-all duration-500 ease-in';
                }

                return (
                  <div
                    key={idx}
                    className={`absolute inset-0 flex items-center justify-center ${animationClass}`}
                  >
                    <span className={`text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight leading-none ${item.color}`}>
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>

            <span>빌려주고,</span>
          </h1>

          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-sky-400 tracking-tight leading-[1.2] keep-all drop-shadow-2xl">
            매번 받는 임대 수익!
          </h2>

          {/* Sub Copy */}
          <p className="text-sm sm:text-base text-slate-200 font-normal max-w-3xl mx-auto pt-4 keep-all drop-shadow">
            2,000평 (약 6,600㎡) 지붕 임대 시 <span className="text-sky-400 font-semibold">최대 10억 원</span>의 임대료 수익!
          </p>

          {/* Main Hero CTA: 수익 계산기 바로가기 */}
          <div className="pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button
              onClick={onNavigateCalculator}
              className="w-full sm:w-auto px-8 py-4.5 rounded-2xl bg-sky-500 hover:bg-sky-400 text-white font-black text-sm sm:text-base transition-all shadow-xl shadow-sky-500/30 active:scale-95 cursor-pointer flex items-center justify-center gap-2.5 group"
            >
              <Zap className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
              <span>실시간 수익 계산기 바로가기</span>
              <ArrowRight className="w-4 h-4 text-sky-100 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>
        </div>

      </div>

      {/* 3. Centered Scroll Down Indicator (스크롤 ↓) */}
      <div className="relative z-10 flex flex-col items-center justify-center pt-6">
        <button
          onClick={scrollToNextSection}
          className="group flex flex-col items-center gap-1.5 text-slate-300 hover:text-sky-400 transition-colors cursor-pointer"
        >
          <span className="text-xs font-extrabold tracking-widest uppercase">스크롤</span>
          <div className="p-2 rounded-full bg-slate-900/80 border border-slate-800 group-hover:border-sky-500/50 shadow-lg animate-bounce">
            <ArrowDown className="w-4 h-4 text-sky-400" />
          </div>
        </button>
      </div>

    </section>
  );
};
